/**
 * Templates — one unified gallery of ready-to-use video starters, re-hosted to
 * our CDN and Vivideo-branded. Two kinds of item share the grid:
 *
 *   • PRESETS (mode 'manual') — prompt + model + duration + preview. "Use this"
 *     seeds Home MANUAL mode (model preselected, prompt filled, duration set);
 *     the user optionally drops an image and hits Create.
 *   • STYLES  (mode 'agent')  — HeyGen visual styles (no prompt/model). "Try this
 *     template" applies the style and seeds Home AGENT/auto mode via `style_id`.
 *
 * Data comes from v2Service.listTemplates() — the `v2-list-templates` endpoint
 * with a bundled static catalogue (public/templates.json) fallback. Categories +
 * pagination are server-driven; agent styles live under the "styles" category.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Search, Sparkles, X, Play, Clock, RectangleHorizontal, Cpu, Image as ImageIcon,
  LayoutTemplate, Tag, ShieldCheck, Zap,
} from 'lucide-react';
import { cn } from '@/v2/utils';
import { v2Service } from '@/api/v2Service';
import type { V2Template, V2TemplateCategory } from '@/api/v2Types';
import { seedHomeManual, seedHomeAgent, writeAppliedTemplate } from '@/v2/seed';
import { PreviewPlayer } from '@/v2/components/PreviewPlayer';
import { getModelById, modelForOperation, OPERATION_LABELS, type OperationType } from '@/v2/data/models';
import { trackAmpTemplateApplied } from '@/services/amplitudeService';

const PAGE_SIZE = 24;

const isPortrait = (ar?: string) => ['9:16', '3:4', '2:3', '9:21'].includes((ar || '').trim());
const isAgent = (tpl: V2Template) => tpl.mode === 'agent';

function titleCase(slug: string): string {
  return slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function modelName(id: string, op: string): string {
  const m = getModelById(id);
  return (m && modelForOperation(m, op as OperationType).name) || id;
}
function opLabel(op: string): string {
  return OPERATION_LABELS[op as OperationType] || op;
}
function styleTags(tpl: V2Template): string[] {
  return (tpl.tags || '').split(';').map((s) => s.trim()).filter(Boolean);
}

// -------------------------------------------------------------------------- //
function TemplateCard({ tpl, onOpen }: { tpl: V2Template; onOpen: () => void }) {
  const { t } = useTranslation('v2');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hovered, setHovered] = useState(false);
  const portrait = isPortrait(tpl.aspect_ratio);
  const agent = isAgent(tpl);

  return (
    <button
      onClick={onOpen}
      onMouseEnter={() => { setHovered(true); void videoRef.current?.play().catch(() => undefined); }}
      onMouseLeave={() => { setHovered(false); videoRef.current?.pause(); }}
      className="group text-left w-full break-inside-avoid mb-6"
    >
      <div
        className={cn(
          'relative rounded-2xl overflow-hidden bg-surface-light transition-all duration-300 group-hover:-translate-y-0.5',
          portrait ? 'aspect-[9/16]' : 'aspect-video',
          'group-hover:ring-2 group-hover:ring-primary/50 group-hover:shadow-glow-sm'
        )}
      >
        {tpl.thumbnail_url && (
          <img
            src={tpl.thumbnail_url}
            alt={tpl.title}
            loading="lazy"
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            className={cn('absolute inset-0 w-full h-full object-cover transition-opacity duration-300 select-none', hovered && tpl.video_url ? 'opacity-0' : 'opacity-100')}
          />
        )}
        {tpl.video_url && (
          <video
            ref={videoRef}
            src={tpl.video_url}
            poster={tpl.thumbnail_url || undefined}
            muted loop playsInline preload="none"
            controlsList="nodownload noremoteplayback"
            disablePictureInPicture
            draggable={false}
            onContextMenu={(e) => e.preventDefault()}
            className={cn('absolute inset-0 w-full h-full object-cover transition-opacity duration-300 pointer-events-none select-none', hovered ? 'opacity-100' : 'opacity-0')}
          />
        )}
        {/* Manual presets surface model + duration; agent styles get an
            "Auto mode" tag so users can tell the two kinds apart at a glance. */}
        {agent ? (
          <span className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/80 backdrop-blur text-white text-[10px] font-semibold">
            <Zap className="w-3 h-3 fill-white" /> {t('templates.autoMode')}
          </span>
        ) : (
          <>
            <span className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-white text-[10px] font-semibold">
              <Cpu className="w-3 h-3" /> {modelName(tpl.vivideo_model_id, tpl.operation)}
            </span>
            {tpl.duration && (
              <span className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-white text-[10px] font-semibold">
                <Clock className="w-3 h-3" /> {tpl.duration}
              </span>
            )}
          </>
        )}
        <span className={cn('absolute inset-0 flex items-center justify-center transition-opacity', hovered ? 'opacity-100' : 'opacity-0')}>
          <span className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
            <Play className="w-4 h-4 text-black fill-black ml-0.5" />
          </span>
        </span>
      </div>
      <p className="text-sm font-semibold text-text-primary mt-2 truncate">{tpl.title}</p>
      <p className="text-xs text-text-tertiary truncate">
        {agent ? (styleTags(tpl).map(titleCase).join(' · ') || t('templates.styleFallback')) : tpl.prompt}
      </p>
    </button>
  );
}

// -------------------------------------------------------------------------- //
function TemplatePreviewModal({ tpl, onUse, onClose }: {
  tpl: V2Template; onUse: () => void; onClose: () => void;
}) {
  const { t } = useTranslation('v2');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const portrait = isPortrait(tpl.aspect_ratio);
  const agent = isAgent(tpl);
  const needsImage = !agent && tpl.operation !== 'text_to_video';

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in" onClick={onClose}>
      <div
        className={cn(
          'w-full rounded-3xl overflow-hidden bg-[#15151d] border border-white/10 shadow-2xl animate-slide-up max-h-[92vh]',
          portrait
            // Portrait: on desktop the clip sits BESIDE a scrollable details
            // column (no cramped narrow prompt); on mobile it stacks + scrolls.
            ? 'max-w-md sm:max-w-4xl flex flex-col sm:flex-row overflow-y-auto sm:overflow-hidden'
            : 'max-w-3xl overflow-y-auto'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={cn(
          'relative bg-black shrink-0',
          portrait
            ? 'w-full sm:w-auto h-[52vh] sm:h-[88vh] aspect-[9/16] mx-auto sm:mx-0'
            : 'aspect-video w-full'
        )}>
          <PreviewPlayer src={tpl.video_url} poster={tpl.thumbnail_url} portrait={portrait} autoPlay />
          <button
            onClick={onClose}
            aria-label={t('common.close', { defaultValue: 'Close' })}
            className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/70 hover:bg-black/90 ring-1 ring-white/20 flex items-center justify-center text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className={cn('p-5 sm:p-6', portrait && 'sm:flex-1 sm:min-w-0 sm:h-[88vh] sm:overflow-y-auto')}>
          <div className={cn('flex gap-4', portrait ? 'flex-col' : 'flex-col sm:flex-row sm:items-start')}>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-bold text-text-primary">{tpl.title}</h3>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {agent ? (
                  <>
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/15 border border-primary/40 text-[11px] font-semibold text-primary">
                      <Zap className="w-3 h-3 fill-primary" /> {t('templates.autoMode')}
                    </span>
                    {styleTags(tpl).map((tag) => (
                      <span key={tag} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-semibold text-text-secondary">
                        <Tag className="w-3 h-3" /> {titleCase(tag)}
                      </span>
                    ))}
                    {tpl.aspect_ratio && (
                      <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-semibold text-text-secondary">
                        <RectangleHorizontal className="w-3 h-3" /> {tpl.aspect_ratio}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-semibold text-text-secondary">
                      <Cpu className="w-3 h-3" /> {modelName(tpl.vivideo_model_id, tpl.operation)}
                    </span>
                    {tpl.duration && (
                      <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-semibold text-text-secondary">
                        <Clock className="w-3 h-3" /> {tpl.duration}
                      </span>
                    )}
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-semibold text-text-secondary">
                      <RectangleHorizontal className="w-3 h-3" /> {tpl.aspect_ratio}
                    </span>
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-semibold text-text-secondary">
                      {opLabel(tpl.operation)}
                    </span>
                  </>
                )}
              </div>
              {!agent && tpl.prompt && (
                <div className={cn(
                  'mt-3 overflow-y-auto rounded-xl bg-black/30 border border-white/10 p-3',
                  // Beside a tall portrait clip the prompt has room to breathe.
                  portrait ? 'max-h-40 sm:max-h-[46vh]' : 'max-h-40'
                )}>
                  <p className="text-xs text-text-secondary whitespace-pre-wrap leading-relaxed">{tpl.prompt}</p>
                </div>
              )}
              {needsImage && (
                <p className="flex items-center gap-1.5 mt-2 text-[11px] text-text-tertiary">
                  <ImageIcon className="w-3.5 h-3.5" /> {t('templates.bringYourImage')}
                </p>
              )}
              <p className="flex items-start gap-1.5 mt-2 text-[11px] text-text-tertiary">
                <ShieldCheck className="w-3.5 h-3.5 mt-px shrink-0" /> {t('templates.watermarkNote')}
              </p>
            </div>
            <div className={cn('shrink-0 flex gap-2 w-full', portrait ? 'flex-col' : 'sm:flex-col sm:w-auto')}>
              <button
                onClick={onUse}
                className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-primary to-accent text-white text-sm font-bold hover:opacity-95 hover:shadow-glow transition-all"
              >
                <Sparkles className="w-4 h-4" /> {agent ? t('templates.tryThisTemplate') : t('templates.useThis')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// -------------------------------------------------------------------------- //
function SkeletonTile({ portrait }: { portrait?: boolean }) {
  return (
    <div className="break-inside-avoid mb-6">
      <div className={cn('rounded-2xl bg-surface-light animate-pulse', portrait ? 'aspect-[9/16]' : 'aspect-video')} />
      <div className="h-3.5 w-2/3 rounded bg-surface-light animate-pulse mt-2.5" />
      <div className="h-2.5 w-1/3 rounded bg-surface-light animate-pulse mt-1.5" />
    </div>
  );
}
const SKELETON_PORTRAIT = new Set([1, 4, 6]);

export function TemplatesPage() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const [items, setItems] = useState<V2Template[]>([]);
  const [categories, setCategories] = useState<V2TemplateCategory[]>([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [preview, setPreview] = useState<V2Template | null>(null);
  // Guards a stale response from overwriting a newer filter's results.
  const reqIdRef = useRef(0);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(id);
  }, [search]);

  const load = useCallback(async (reset: boolean) => {
    const rid = reset ? ++reqIdRef.current : reqIdRef.current;
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const res = await v2Service.listTemplates({
        category: tab === 'all' ? undefined : tab,
        search: debouncedSearch || undefined,
        limit: PAGE_SIZE,
        cursor: reset ? null : cursor,
      });
      if (rid !== reqIdRef.current) return; // superseded
      setCategories(res.categories || []);
      setTotal(res.total || 0);
      setCursor(res.cursor);
      setItems((prev) => (reset ? res.templates : [...prev, ...res.templates]));
    } catch { /* keep whatever we have */ }
    finally {
      if (rid === reqIdRef.current) { setLoading(false); setLoadingMore(false); }
    }
  }, [tab, debouncedSearch, cursor]);

  // Reset + reload whenever the filter changes.
  useEffect(() => { void load(true); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab, debouncedSearch]);

  // "styles" (agent) sorts to the front after "all"; presets keep server order.
  const tabs = useMemo(() => {
    const slugs = categories.map((c) => c.slug);
    const styles = slugs.filter((s) => s === 'styles');
    const rest = slugs.filter((s) => s !== 'styles');
    return ['all', ...styles, ...rest];
  }, [categories]);

  const catLabel = (slug: string) => t(`templates.categories.${slug}`, { defaultValue: titleCase(slug) });

  const use = (tpl: V2Template) => {
    trackAmpTemplateApplied({ templateId: tpl.template_id, templateName: tpl.title });
    if (isAgent(tpl)) {
      // HeyGen visual style → persist + seed Home AGENT/auto mode via style_id.
      const styleId = tpl.style_id || '';
      writeAppliedTemplate({ id: styleId, name: tpl.title, img: tpl.thumbnail_url, video: tpl.video_url });
      seedHomeAgent({ styleId, styleName: tpl.title, styleImg: tpl.thumbnail_url, stylePreviewVideo: tpl.video_url });
    } else {
      seedHomeManual({
        modelId: tpl.vivideo_model_id,
        operation: tpl.operation,
        prompt: tpl.prompt,
        duration: tpl.duration,
        aspectRatio: tpl.aspect_ratio,
        resolution: tpl.resolution,
        templateId: tpl.template_id,
        templateName: tpl.title,
      });
    }
    navigate('/');
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold text-text-primary">
          {t('templates.heading')}<span className="gradient-text">.</span>
        </h1>
        <p className="text-xs text-text-tertiary mt-1">{t('templates.subtitle')}</p>
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('templates.searchPlaceholder')}
          className="w-full bg-surface border border-glass-border rounded-2xl pl-11 pr-4 py-3 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:border-primary/50 transition-colors"
        />
      </div>

      {/* Category tabs */}
      {loading && !categories.length ? (
        <div className="flex items-center gap-2.5 mb-5 pt-2 pb-2">
          {[110, 96, 124, 88, 104].map((w, i) => (
            <div key={i} className="h-10 rounded-full bg-surface-light animate-pulse" style={{ width: w }} />
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-2.5 mb-5 overflow-x-auto scrollbar-hide pt-2 -mt-2 px-2 -mx-2 pb-2">
          {tabs.map((slug) => {
            const active = tab === slug;
            const count = slug === 'all' ? total : (categories.find((c) => c.slug === slug)?.count ?? 0);
            return (
              <button
                key={slug}
                onClick={() => setTab(slug)}
                className={cn(
                  'group/tab flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200',
                  active
                    ? 'bg-gradient-to-r from-primary to-accent text-white shadow-glow scale-[1.03]'
                    : 'glass text-text-secondary hover:text-text-primary hover:-translate-y-0.5 hover:shadow-glow-sm'
                )}
              >
                {slug === 'all' ? t('templates.allTemplates') : catLabel(slug)}
                <span className={cn(
                  'px-1.5 py-0.5 rounded-full text-[10px] font-bold tabular-nums transition-colors',
                  active ? 'bg-white/20 text-white' : 'bg-surface-light text-text-tertiary group-hover/tab:text-text-secondary'
                )}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Watermark disclaimer — the badge is only on these previews, never on
          the videos the user generates. */}
      <div className="flex items-start gap-2 mb-8 px-4 py-3 rounded-xl bg-primary/5 border border-primary/15 text-xs text-text-secondary">
        <ShieldCheck className="w-4 h-4 text-primary mt-px shrink-0" />
        <span>{t('templates.watermarkNote')}</span>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="columns-2 lg:columns-3 gap-6">
          {Array.from({ length: 9 }).map((_, i) => (
            <SkeletonTile key={i} portrait={SKELETON_PORTRAIT.has(i)} />
          ))}
        </div>
      ) : items.length ? (
        <>
          <div className="columns-2 lg:columns-3 gap-6">
            {items.map((tpl) => (
              <TemplateCard key={tpl.template_id} tpl={tpl} onOpen={() => setPreview(tpl)} />
            ))}
          </div>
          {cursor && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() => void load(false)}
                disabled={loadingMore}
                className="px-6 py-2.5 rounded-full glass text-sm font-semibold text-text-secondary hover:text-text-primary hover:shadow-glow-sm transition-all disabled:opacity-60"
              >
                {loadingMore ? t('templates.loading') : t('templates.loadMore')}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="rounded-2xl border border-dashed border-glass-border p-12 text-center">
          <LayoutTemplate className="w-8 h-8 text-text-tertiary mx-auto mb-3" />
          <p className="text-sm font-semibold text-text-primary mb-1">{t('templates.emptyTitle')}</p>
          <p className="text-xs text-text-tertiary">{t('templates.emptySubtitle')}</p>
        </div>
      )}

      {preview && (
        <TemplatePreviewModal tpl={preview} onUse={() => use(preview)} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}
